// The quick edit's model (timesheet rules §3, §6): one person's day from time.day, and what the dialog
// draws (QuickEditView). Pure: no calls. It finds the day's problem and opens on its likeliest fix and
// reason; keeps every change typed (Fix, a part's Starts and Ends, Add time) stacked until Save; checks
// each time as the server will (not a time, an end before the start, later than now, into a neighbour);
// asks first for a time past midnight or a part over 12 h; draws what Save would write as ghosts; and
// says in one footer sentence what Save writes and what the day's paid minutes become (estimated here
// from the parts; the server's figure replaces it after Save). saveRequests is what Save sends;
// refused maps the server's refusal back onto the fields (or, for someone saving first, keeps the
// changes and asks to save again). Minutes only, never pay.
import { localDateOf } from '@flowology/kernel';
import { parseTypedTime } from '@flowology/ui-web/fl';
import type {
  ChoiceView,
  DayPartView,
  QuickEditAction,
  QuickEditField,
  QuickEditView,
  TimeFieldView,
} from '@flowology/ui-web/timesheet';
import { checkOf, dayProblems, type DayProblem } from './problems.ts';
import type {
  DayChange,
  NonJobCode,
  PayPeriod,
  TimeDay,
  TimeEntry,
  TimeHistoryItem,
} from './timesheet-types.ts';
import { capital, clockOn, clockWord, codeWords, dayWords, minutesText, periodWords } from './time-words.ts';

export interface BookedVisit {
  id: string;
  jobId: string | null;
  customer: string;
  startMinute: number;
  endMinute: number;
  drawn: 'done' | 'working' | 'booked';
  /** Its own booked times ("11 a.m.–3:30 p.m.") and the job's number ("#24611"), as the board words them. */
  time?: string;
  job?: string;
  /** The arrival window promised, where it isn't the visit's own times ("11 a.m.–1 p.m."). */
  arrival?: string;
}

export interface QuickEditInput {
  staffId: string;
  day: TimeDay;
  /** The company's today. */
  today: string;
  /** Now, as a minute of the day's clock (read only when the day is today). */
  nowMinute: number;
  person: { name: string; first: string; pen?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | null };
  /** The board's visits for them that day, at their booked windows. */
  booked: BookedVisit[];
  /** The company's non-job times (Add time). */
  codes: NonJobCode[];
  /** The pay period the day is in (its locked and sent words). */
  period: PayPeriod | null;
  timeZone: string;
  /** A late call: past midnight is expected, so it isn't asked about. */
  lateCall?: boolean;
  /** The latest change to the day (time.history), for the notes. */
  lastChange?: TimeHistoryItem | null;
  /** Whether the iPad still holds taps isn't given by the server yet (§9.4): said so in the notes. */
  device?: 'unknown';
  /** The daily overtime line in paid minutes (8 h). */
  overtimeAfter?: number;
}

export interface QuickEditState {
  version: number;
  fix: string | null;
  fixText: string;
  /** The part pressed: its Starts and Ends are under Change. */
  selected: string | null;
  /** What was typed into each part's Starts and Ends, by part. */
  edits: Record<string, { start?: string; end?: string }>;
  add: string | null;
  addFrom: string;
  addTo: string;
  reason: string | null;
  reasonTouched: boolean;
  other: string;
  /** The ask (past midnight, over 12 h) that was confirmed, by its words. */
  confirmed: string | null;
  /** Someone else saved first: their words, in red under the body. */
  conflict: string | null;
  /** The server's words for a field, by field key, until it's typed in again. */
  server: Record<string, string>;
  /**
   * Someone else saved first (QA-774): the changes this person had made, kept to save again on the day
   * as it is now, so Save again replaces theirs. A change typed since on the same part wins.
   */
  kept: KeptChange[];
  dirty: boolean;
}

/** A change kept through a clash: a change to the day, or a check settled as it stands. */
export type KeptChange = DayChange | { kind: 'correct'; entryId: string };

export const REASONS: ChoiceView[] = [
  { id: 'forgot', label: 'Forgot to clock out' },
  { id: 'wrongTap', label: 'Tapped the wrong thing' },
  { id: 'ipad', label: 'The iPad got it wrong' },
  { id: 'right', label: 'Checked: the time is right' },
  { id: 'notTapped', label: 'Didn’t tap it' },
  { id: 'other', label: 'Other' },
];

type Fix = ChoiceView & {
  /** What it saves; 'typed' takes the time typed under it; 'correct' settles the flag with 0 min. */
  does: { kind: 'change'; change: DayChange } | { kind: 'typed' } | { kind: 'correct' };
  reason: string;
};

/** The day's problems, the first being the one the dialog opens on. */
export const problemsOf = (input: QuickEditInput) => dayProblems(input.day, input.today);

const isToday = (input: QuickEditInput) => input.day.date === input.today;
const bookedFor = (input: QuickEditInput, e: TimeEntry) =>
  input.booked.find(
    (b) => (e.jobId ? b.jobId === e.jobId : false) || (!!e.customer && b.customer === e.customer),
  );

/** The suggested fixes for a problem, likeliest first, and which is chosen when the dialog opens. */
export function fixesFor(input: QuickEditInput, p: DayProblem): { fixes: Fix[]; chosen: string | null } {
  const date = input.day.date;
  if (p.kind === 'notEnded')
    return {
      fixes: [
        {
          id: 'endDay',
          label: `End the day at ${clockOn(p.lastEnd, date)}`,
          description: 'No paid minutes change',
          does: { kind: 'change', change: { kind: 'endDay', minute: p.lastEnd } },
          reason: 'forgot',
        },
        {
          id: 'other',
          label: 'Another time',
          description: 'Type when the day ended',
          does: { kind: 'typed' },
          reason: 'forgot',
        },
      ],
      chosen: 'endDay',
    };
  const e = p.entry;
  const b = bookedFor(input, e);
  const fixes: Fix[] = [];
  if (p.kind === 'flag' && e.endMinute !== null)
    fixes.push({
      id: 'right',
      label: `${clockOn(e.endMinute, date)} is right`,
      description: 'Settles the check: 0 min change',
      does: { kind: 'correct' },
      reason: 'right',
    });
  if (b && b.endMinute > e.startMinute && b.endMinute !== e.endMinute) {
    const change: DayChange = { kind: 'setEnd', entryId: e.id, minute: b.endMinute };
    const sim = simulate(input, [change]);
    fixes.push({
      id: 'booked',
      label:
        p.kind === 'missed'
          ? `It ended at ${clockOn(b.endMinute, date)}, the booked end`
          : `${capital(kindWord(e))} ran to ${clockOn(b.endMinute, date)}, the booked end`,
      description: deltaWords(sim.paidAfter - input.day.split.paid),
      does: { kind: 'change', change },
      reason: p.kind === 'missed' ? 'forgot' : 'wrongTap',
    });
  }
  fixes.push({
    id: 'other',
    label: 'Another time',
    description: `Type when ${p.kind === 'missed' ? 'it' : 'it really'} ended`,
    does: { kind: 'typed' },
    reason: p.kind === 'missed' ? 'forgot' : 'wrongTap',
  });
  // Where the likeliest fix is only a guess (still running at the next dispatch), nothing is chosen.
  const chosen = p.kind === 'flag' && p.code === 'closed_by_dispatch' ? null : (fixes[0]?.id ?? null);
  return { fixes, chosen };
}

const kindWord = (e: TimeEntry) => (e.type === 'work' ? 'work' : e.type === 'drive' ? 'the drive' : e.name);

function deltaWords(d: number): string {
  if (d > 0) return `Adds ${minutesText(d)} paid`;
  if (d < 0) return `Takes off ${minutesText(-d)} paid`;
  return 'No paid minutes change';
}

/** Opening isn't a change: the likeliest fix and its reason are chosen, and nothing is dirty. */
export function initialQuickEdit(input: QuickEditInput, opts: { fix?: string } = {}): QuickEditState {
  const first = problemsOf(input)[0];
  const f = first && !input.day.locked ? fixesFor(input, first) : null;
  const fix = opts.fix && f?.fixes.some((x) => x.id === opts.fix) ? opts.fix : (f?.chosen ?? null);
  return {
    version: input.day.version,
    fix,
    fixText: '',
    selected: null,
    edits: {},
    add: null,
    addFrom: '',
    addTo: '',
    reason: fix ? (f!.fixes.find((x) => x.id === fix)?.reason ?? null) : null,
    reasonTouched: false,
    other: '',
    confirmed: null,
    conflict: null,
    server: {},
    kept: [],
    dirty: false,
  };
}

/** What a press does to the state; ‹ ›, Timesheet › and the opener are the page's, not the model's. */
export function quickEditReduce(
  input: QuickEditInput,
  s: QuickEditState,
  a: QuickEditAction,
): QuickEditState {
  switch (a.type) {
    case 'part':
      return input.day.locked ? s : { ...s, selected: a.id };
    case 'back':
      return { ...s, selected: null };
    case 'fix': {
      const first = problemsOf(input)[0];
      const fix = first ? fixesFor(input, first).fixes.find((x) => x.id === a.id) : undefined;
      return {
        ...s,
        fix: a.id,
        reason: s.reasonTouched ? s.reason : (fix?.reason ?? s.reason),
        server: without(s.server, 'fixTime'),
        dirty: true,
      };
    }
    case 'text': {
      const server = without(s.server, keyOf(a.field, s.selected));
      if (a.field === 'fixTime') return { ...s, fixText: a.text, server, dirty: true };
      if (a.field === 'addFrom') return { ...s, addFrom: a.text, server, dirty: true };
      if (a.field === 'addTo') return { ...s, addTo: a.text, server, dirty: true };
      if (a.field === 'whyOther') return { ...s, other: a.text, dirty: true };
      if (!s.selected) return s;
      const was = s.edits[s.selected] ?? {};
      return {
        ...s,
        edits: { ...s.edits, [s.selected]: { ...was, [a.field]: a.text } },
        server,
        dirty: true,
      };
    }
    case 'add':
      return { ...s, add: a.code, server: without(without(s.server, 'addFrom'), 'addTo'), dirty: true };
    case 'addLunch':
      return { ...s, add: 'lunch', dirty: true };
    case 'reason':
      return { ...s, reason: a.id, reasonTouched: true, dirty: true };
    case 'confirm':
      return { ...s, confirmed: pendingOf(input, s).ask?.text ?? null };
    case 'decline': {
      // "Before <start>": the long reading isn't it, so the time goes back to its field with words
      const ask = pendingOf(input, s).ask;
      if (!ask?.declined) return s;
      if (ask.key.startsWith('kept:')) {
        const i = Number(ask.key.slice(5));
        return { ...s, kept: s.kept.filter((_, j) => j !== i), confirmed: null, dirty: true };
      }
      const field = ask.key === 'fix' ? 'fixTime' : ask.key;
      return {
        ...s,
        ...(ask.key === 'fix' ? { fix: 'other' } : {}),
        server: { ...s.server, [field]: ask.declined },
        confirmed: null,
        dirty: true,
      };
    }
    default:
      return s;
  }
}

const without = (o: Record<string, string>, k: string) => {
  if (!(k in o)) return o;
  const { [k]: _, ...rest } = o;
  return rest;
};
const keyOf = (field: QuickEditField, selected: string | null) =>
  field === 'start' || field === 'end' ? `${field}:${selected ?? ''}` : field;

// ---- the stack of changes

interface Pending {
  change: DayChange | { kind: 'correct'; entry: TimeEntry };
  words: string;
  /** The field it came from, for the server's words about it. */
  key: string;
}

/**
 * Ask first: a time past midnight, or a part over 12 h. Over 12 h past midnight (an end typed before
 * the start reads as the next day) is a question with both readings, neither chosen (QA-774).
 */
interface Ask {
  text: string;
  /** The field the change came from. */
  key: string;
  /** The long reading's button; the plain "Yes, that's right" otherwise. */
  yes: string;
  /** The other reading's button, and the words its field then says. */
  no?: string;
  declined?: string;
}

interface Stack {
  changes: Pending[];
  /** Words under a field, by field key. */
  errors: Record<string, string>;
  /** A field that needs a time and has none yet. */
  missing: boolean;
  /** The first ask (past midnight, over 12 h), if any. */
  ask: Ask | null;
}

export function pendingOf(input: QuickEditInput, s: QuickEditState): Stack {
  const st: Stack = { changes: [], errors: { ...s.server }, missing: false, ask: null };
  const { day } = input;
  if (day.locked || !day.entries.length) return st;
  const date = day.date;
  const first = problemsOf(input)[0];

  // Kept through a clash (QA-774): on the day as it is now, unless something typed since replaces it
  s.kept.forEach((k, i) => {
    const key = `kept:${i}`;
    if (k.kind === 'correct') {
      const e = day.entries.find((x) => x.id === k.entryId);
      if (e && checkOf(e) && !s.fix)
        st.changes.push({
          change: { kind: 'correct', entry: e },
          words: `${clockOn(e.endMinute!, date)} stays; the check clears`,
          key,
        });
      return;
    }
    if (replacedSince(input, s, k)) return;
    if (k.kind === 'setStart' || k.kind === 'setEnd') {
      const e = day.entries.find((x) => x.id === k.entryId);
      if (!e || (k.kind === 'setEnd' ? e.endMinute : e.startMinute) === k.minute) return;
    }
    const err = k.kind === 'addPart' ? checkAdd(input, k)?.words : check(input, k);
    if (err) st.errors[key] ??= err;
    else push(input, st, k, key);
  });

  // Fix
  if (first && s.fix) {
    const fix = fixesFor(input, first).fixes.find((f) => f.id === s.fix);
    if (fix?.does.kind === 'change') push(input, st, fix.does.change, 'fix');
    else if (fix?.does.kind === 'correct' && first.kind === 'flag')
      st.changes.push({
        change: { kind: 'correct', entry: first.entry },
        words: `${clockOn(first.entry.endMinute!, date)} stays; the check clears`,
        key: 'fix',
      });
    else if (fix?.does.kind === 'typed') {
      const near =
        first.kind === 'notEnded' ? first.lastEnd : (first.entry.endMinute ?? first.entry.startMinute + 60);
      const m = typed(st, s.fixText, near, 'fixTime');
      if (m !== null) {
        const change: DayChange =
          first.kind === 'notEnded'
            ? { kind: 'endDay', minute: m }
            : { kind: 'setEnd', entryId: first.entry.id, minute: m };
        const err = check(input, change);
        if (err) st.errors.fixTime ??= err;
        else push(input, st, change, 'fixTime');
      }
    }
  }

  // Change: each part's Starts and Ends, as typed
  for (const e of day.entries) {
    const ed = s.edits[e.id];
    if (!ed) continue;
    if (ed.start !== undefined && !e.fromMidnight) {
      const m = typed(st, ed.start, e.startMinute, `start:${e.id}`);
      if (m !== null && m !== e.startMinute) {
        const change: DayChange = { kind: 'setStart', entryId: e.id, minute: m };
        const err = check(input, change);
        if (err) st.errors[`start:${e.id}`] ??= err;
        else push(input, st, change, `start:${e.id}`);
      }
    }
    if (ed.end !== undefined && !e.running) {
      const m = typed(st, ed.end, e.endMinute ?? e.startMinute + 60, `end:${e.id}`);
      if (m !== null && m !== e.endMinute) {
        const change: DayChange = { kind: 'setEnd', entryId: e.id, minute: m };
        const err = check(input, change);
        if (err) st.errors[`end:${e.id}`] ??= err;
        else push(input, st, change, `end:${e.id}`);
      }
    }
  }

  // Add time: To is 30 min after From when it's left blank
  if (s.add) {
    const near = day.entries.at(-1)?.startMinute ?? 720;
    const from = typed(st, s.addFrom, near, 'addFrom', { nextDay: false });
    const to = s.addTo.trim()
      ? typed(st, s.addTo, from ?? near, 'addTo', { nextDay: false })
      : from !== null
        ? from + 30
        : null;
    if (from !== null && to !== null) {
      const change: DayChange = { kind: 'addPart', code: s.add, from, to };
      const err = checkAdd(input, change);
      if (err) st.errors[err.field] ??= err.words;
      else push(input, st, change, 'addFrom');
    }
  }
  return st;
}

/** A typed time as a minute, or its words under the field; blank is missing, not an error. */
function typed(
  st: Stack,
  text: string,
  near: number,
  key: string,
  opts: { nextDay?: boolean } = {},
): number | null {
  if (st.errors[key]) return null;
  if (!text.trim()) {
    st.missing = true;
    return null;
  }
  const r = parseTypedTime(text, near, opts);
  if (!r.ok) {
    st.errors[key] = notATime(text);
    return null;
  }
  return r.minute;
}

/** A refused time in words (QA-774): "25:99 isn’t a time.", or for words, how to type one. */
export function notATime(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  return /\d/.test(t) && t.length <= 12 ? `${t} isn’t a time.` : 'Type a time like 9:30 a.m.';
}

/** Whether something typed since the clash changes the same part's same end (or the day's end). */
function replacedSince(input: QuickEditInput, s: QuickEditState, k: DayChange): boolean {
  if (k.kind === 'addPart') return s.add === k.code;
  const first = problemsOf(input)[0];
  if (s.fix && first) {
    const fix = fixesFor(input, first).fixes.find((f) => f.id === s.fix);
    const entry = first.kind === 'notEnded' ? null : first.entry.id;
    if (fix && fix.does.kind !== 'correct') {
      if (k.kind === 'endDay') return true;
      if (k.kind === 'setEnd' && k.entryId === entry) return true;
    }
  }
  if (k.kind === 'endDay') return false;
  const typedHere = s.edits[k.entryId];
  return k.kind === 'setStart' ? typedHere?.start !== undefined : typedHere?.end !== undefined;
}

function push(input: QuickEditInput, st: Stack, change: DayChange, key: string) {
  st.changes.push({ change, words: changeWords(input, change), key });
  const ask = askFor(input, change, key);
  if (ask && !st.ask) st.ask = ask;
}

/** A sentence's full stop, once: one ending on "p.m." takes no other. */
const stop = (s: string) => (s.endsWith('.') ? s : `${s}.`);

const entryOf = (input: QuickEditInput, id: string) => input.day.entries.find((e) => e.id === id)!;

function changeWords(input: QuickEditInput, c: DayChange): string {
  const date = input.day.date;
  if (c.kind === 'setEnd') return `${entryOf(input, c.entryId).name} ends ${clockOn(c.minute, date)}`;
  if (c.kind === 'setStart') return `${entryOf(input, c.entryId).name} starts ${clockOn(c.minute, date)}`;
  if (c.kind === 'endDay') return `the day ends ${clockOn(c.minute, date)}`;
  const inside = containing(input.day.entries, c.from, c.to);
  return `${codeWords(c.code, codeOf(input, c.code)?.name)} ${clockWord(c.from)} to ${clockWord(c.to)}${inside ? `, inside ${inside.name}` : ''}`;
}

const codeOf = (input: QuickEditInput, code: string) => input.codes.find((c) => c.code === code);
const containing = (entries: TimeEntry[], from: number, to: number) =>
  entries.find((e) => e.startMinute < from && to < (e.endMinute ?? Infinity));

/** The server's own checks, said first under the field (rules §3 Errors). */
function check(input: QuickEditInput, c: DayChange): string | null {
  const { day } = input;
  const date = day.date;
  const now = isToday(input) ? input.nowMinute : Infinity;
  if (c.kind === 'endDay') {
    if (c.minute > now) return stop(`That’s later than now, ${clockWord(now)}`);
    const last = day.entries.at(-1);
    if (last?.endMinute != null && c.minute < last.endMinute)
      return stop(`That’s before the day’s last tap, ${clockOn(last.endMinute, date)}`);
    return null;
  }
  if (c.kind === 'addPart') return null;
  const i = day.entries.findIndex((e) => e.id === c.entryId);
  const e = day.entries[i]!;
  if (c.minute > now) return stop(`That’s later than now, ${clockWord(now)}`);
  if (c.kind === 'setEnd') {
    if (c.minute <= e.startMinute) return `Ends before it starts (${clockOn(e.startMinute, date)}).`;
    const next = day.entries[i + 1];
    if (next && next.endMinute !== null && c.minute >= next.endMinute)
      return stop(`That runs into ${next.name}, which ends ${clockOn(next.endMinute, date)}`);
    if (next && e.endMinute === null && c.minute > next.startMinute)
      return stop(`That runs into ${next.name}, which starts ${clockOn(next.startMinute, date)}`);
    return null;
  }
  if (e.endMinute !== null && c.minute >= e.endMinute)
    return `Starts after it ends (${clockOn(e.endMinute, date)}).`;
  const prev = day.entries[i - 1];
  if (prev && c.minute <= prev.startMinute)
    return stop(`That runs into ${prev.name}, which starts ${clockOn(prev.startMinute, date)}`);
  return null;
}

function checkAdd(
  input: QuickEditInput,
  c: Extract<DayChange, { kind: 'addPart' }>,
): { field: string; words: string } | null {
  const { day } = input;
  if (c.to <= c.from) return { field: 'addTo', words: 'Ends before it starts.' };
  if (c.to >= 1440)
    return { field: 'addTo', words: stop(`It has to end by midnight on ${dayWords(day.date)}`) };
  if (isToday(input) && c.to > input.nowMinute)
    return { field: 'addTo', words: stop(`That’s later than now, ${clockWord(input.nowMinute)}`) };
  // An unpaid break (lunch) has to be inside the working day, as the server says (QA-774)
  const code = codeOf(input, c.code);
  const span = workingDay(day);
  if (code && !code.paid && span && (c.from < span.start || (span.end !== null && c.to > span.end)))
    return {
      field: c.from < span.start ? 'addFrom' : 'addTo',
      words: `${c.code === 'lunch' ? 'A lunch' : capital(codeWords(c.code, code.name))} has to be inside the working day (${clockOn(span.start, day.date)}–${span.end === null ? 'no end yet' : clockOn(span.end, day.date)}).`,
    };
  if (containing(day.entries, c.from, c.to)) return null;
  // it starts inside a part and goes on past that part's end
  const over = day.entries.find(
    (e) => e.startMinute < c.from && e.endMinute !== null && c.from < e.endMinute && c.to > e.endMinute,
  );
  if (over)
    return {
      field: 'addTo',
      words: stop(
        `Goes past the end of ${over.type === 'work' ? 'the work' : over.type === 'drive' ? 'the drive' : over.name}, ${clockOn(over.endMinute!, day.date)}`,
      ),
    };
  const into = day.entries.find((e) => e.startMinute < c.to && c.from < (e.endMinute ?? Infinity));
  if (into)
    return {
      field: 'addFrom',
      words: stop(
        `That runs into ${into.name}, ${clockOn(into.startMinute, day.date)} to ${into.endMinute === null ? 'no end' : clockOn(into.endMinute, day.date)}`,
      ),
    };
  return null;
}

/** The working day: from its first part's start to its end (null while it hasn't one, or midnight closed it). */
function workingDay(day: TimeDay): { start: number; end: number | null } | null {
  const first = day.entries[0];
  const last = day.entries.at(-1);
  if (!first || !last) return null;
  return { start: first.startMinute, end: day.endTrigger === 'midnight' ? null : last.endMinute };
}

/** The part a change ends or starts, and how long it then runs (minutes). */
function lengthAfter(input: QuickEditInput, c: DayChange): { e: TimeEntry; long: number } | null {
  const { day } = input;
  if (c.kind === 'addPart') return null;
  if (c.kind === 'setStart') {
    const e = entryOf(input, c.entryId);
    return e.endMinute === null || day.endTrigger === 'midnight' ? null : { e, long: e.endMinute - c.minute };
  }
  const e =
    c.kind === 'setEnd'
      ? entryOf(input, c.entryId)
      : day.entries.at(-1)?.endMinute === null
        ? day.entries.at(-1)!
        : null;
  return e ? { e, long: c.minute - e.startMinute } : null;
}

/** Over 12 h, the server's own line: it's refused unless it says it's meant. */
const overTwelve = (input: QuickEditInput, c: DayChange) => (lengthAfter(input, c)?.long ?? 0) > 720;

/**
 * Ask first (rules §3): an end past midnight on a day that isn't a late call, or a part over 12 h. An
 * end that runs over 12 h into the next day is most likely a time before the start, so it's asked with
 * both readings and neither chosen (QA-774).
 */
function askFor(input: QuickEditInput, c: DayChange, key: string): Ask | null {
  const { day } = input;
  const date = day.date;
  if (c.kind === 'addPart') return null;
  const l = lengthAfter(input, c);
  if (!l) return null;
  const { e, long } = l;
  if (c.kind !== 'setStart' && c.minute >= 1440 && long > 720) {
    const start = clockOn(e.startMinute, date);
    const end = clockWord(c.minute);
    return {
      text: `Did you mean before ${start}? As typed, ${end} is the next day: ${minutesText(long)} of ${e.name}.`,
      key,
      yes: `Ends ${end} the next day (${minutesText(long)})`,
      no: `Before ${start}: change the time`,
      declined: `Ends before it starts (${start}). Type when it really ended.`,
    };
  }
  if (c.kind !== 'setStart' && c.minute >= 1440 && !input.lateCall)
    return {
      text: `That’s ${clockOn(c.minute, date)}: ${minutesText(long)} of ${e.name}, past midnight.`,
      key,
      yes: 'Yes, that’s right',
    };
  if (long > 720)
    return { text: `That’s ${minutesText(long)} of ${e.name}: over 12 h.`, key, yes: 'Yes, that’s right' };
  return null;
}

// ---- what Save would write: the parts redrawn, and the paid minutes after

interface Seg {
  id: string;
  entry: TimeEntry | null;
  kind: DayPartView['kind'];
  start: number;
  end: number | null;
  paid: boolean;
  name: string;
  changed: boolean;
}

const partKind = (e: { type: string; nonJobCode: string | null }): DayPartView['kind'] =>
  e.type === 'work'
    ? 'work'
    : e.type === 'drive'
      ? 'drive'
      : e.nonJobCode === 'lunch'
        ? 'lunch'
        : e.nonJobCode === 'supply_house'
          ? 'supply_house'
          : 'shop';

function simulate(
  input: QuickEditInput,
  changes: DayChange[],
): { segs: Seg[]; paidAfter: number; moves: string[] } {
  const segs: Seg[] = input.day.entries.map((e) => ({
    id: e.id,
    entry: e,
    kind: partKind(e),
    start: e.startMinute,
    end: e.endMinute,
    paid: e.paid,
    name: e.name,
    changed: false,
  }));
  const moves: string[] = [];
  const move = (d: number, from: Seg, to: Seg) => {
    if (d && from.paid === to.paid)
      moves.push(`${minutesText(Math.abs(d))} move from ${from.name} to ${to.name}`);
  };
  for (const c of changes) {
    if (c.kind === 'setEnd' || (c.kind === 'endDay' && segs.at(-1)?.end === null)) {
      const i = c.kind === 'setEnd' ? segs.findIndex((x) => x.id === c.entryId) : segs.length - 1;
      const s = segs[i]!;
      const old = s.end;
      s.end = c.minute;
      s.changed = true;
      const n = segs[i + 1];
      if (n && old !== null && n.start === old) {
        n.start = c.minute;
        n.changed = true;
        if (c.minute > old) move(c.minute - old, n, s);
        else move(old - c.minute, s, n);
      }
    } else if (c.kind === 'setStart') {
      const i = segs.findIndex((x) => x.id === c.entryId);
      const s = segs[i]!;
      const old = s.start;
      s.start = c.minute;
      s.changed = true;
      const p = segs[i - 1];
      if (p && p.end === old) {
        p.end = c.minute;
        p.changed = true;
        if (c.minute < old) move(old - c.minute, p, s);
        else move(c.minute - old, s, p);
      }
    } else if (c.kind === 'addPart') {
      const code = codeOf(input, c.code);
      const add: Seg = {
        id: `add:${c.code}:${c.from}`,
        entry: null,
        kind: partKind({ type: 'non_job', nonJobCode: c.code }),
        start: c.from,
        end: c.to,
        paid: code?.paid ?? false,
        name: codeWords(c.code, code?.name),
        changed: true,
      };
      const i = segs.findIndex((x) => x.start < c.from && c.to < (x.end ?? Infinity));
      if (i >= 0) {
        const s = segs[i]!;
        const after: Seg = { ...s, id: `${s.id}:after`, start: c.to, changed: true };
        s.end = c.from;
        s.changed = true;
        segs.splice(i + 1, 0, add, after);
      } else {
        segs.push(add);
        segs.sort((a, b) => a.start - b.start);
      }
    }
  }
  const paidOf = (list: { start: number; end: number | null; paid: boolean }[]) =>
    list.reduce((n, x) => n + (x.paid && x.end !== null ? x.end - x.start : 0), 0);
  const before = paidOf(
    input.day.entries.map((e) => ({ start: e.startMinute, end: e.endMinute, paid: e.paid })),
  );
  return { segs, paidAfter: input.day.split.paid + paidOf(segs) - before, moves };
}

// ---- the view

export function quickEditView(input: QuickEditInput, s: QuickEditState): QuickEditView {
  const { day, person } = input;
  const date = day.date;
  const dayW = dayWords(date);
  const today = isToday(input);
  const problems = day.locked ? [] : problemsOf(input);
  const first = problems[0];
  const st = pendingOf(input, s);
  const dayChanges = st.changes.flatMap((p) => (p.change.kind === 'correct' ? [] : [p.change]));
  const sim = simulate(input, dayChanges);

  const clocked: DayPartView[] = day.entries.map((e) => ({
    id: e.id,
    kind: partKind(e),
    startMinute: e.startMinute,
    endMinute: e.endMinute,
    label: e.type === 'drive' ? undefined : e.type === 'work' ? (e.customer ?? 'Work') : undefined,
    name: `${capital(e.name)}, ${clockOn(e.startMinute, date)} to ${e.endMinute === null ? (e.running ? 'now' : 'no end') : clockOn(e.endMinute, date)}`,
    state: e.endMinute !== null ? 'done' : e.running ? 'running' : 'noEnd',
    toCheck: checkOf(e),
    paid: e.paid,
    pen: person.pen ?? null,
    isSelected: s.selected === e.id,
  }));
  const ghosts: DayPartView[] = sim.segs
    .filter((g) => g.changed)
    .map((g) => ({
      id: g.id,
      kind: g.kind,
      startMinute: g.start,
      endMinute: g.end,
      name: `${capital(g.name)}, ${clockOn(g.start, date)} to ${g.end === null ? 'no end' : clockOn(g.end, date)}`,
      state: 'done',
      toCheck: null,
      paid: g.paid,
      pen: person.pen ?? null,
      isGhost: true,
    }));
  const lastMinute = Math.max(
    0,
    ...day.entries.map((e) => e.endMinute ?? e.startMinute),
    ...ghosts.map((g) => g.endMinute ?? 0),
  );

  const strip: QuickEditView['strip'] = {
    booked: input.booked.map((b) => ({
      id: b.id,
      startMinute: b.startMinute,
      endMinute: b.endMinute,
      label: b.customer,
      drawn: b.drawn,
      ...(b.time ? { time: b.time } : {}),
      ...(b.job ? { job: b.job } : {}),
      ...(b.arrival ? { arrival: b.arrival } : {}),
    })),
    clocked,
    ghosts,
    nowMinute: today ? input.nowMinute : null,
    midnight: lastMinute > 1440 ? clockOn(1440, date) : null,
    pressable: !day.locked,
  };

  const base = {
    title: titleOf(input, first),
    sub: subOf(input, first),
    steps: { back: true, forward: date < input.today },
    strip,
    notes: notesOf(input, problems),
    fix: null,
    change: null,
    add: null,
    why: null,
    ask: null,
    pending: null,
    conflict: null,
    discardText: `Discard your change to ${dayW}?`,
    isDirty: false,
  };
  if (day.locked)
    return {
      ...base,
      title: `${person.name} · ${dayW}`,
      body: { kind: 'locked', words: lockedWords(input) },
      footer: { note: '', canSave: false, submitLabel: 'Save' },
    };
  if (!day.entries.length)
    return {
      ...base,
      body: { kind: 'empty', words: `No taps on ${dayW}.` },
      footer: { note: '', canSave: false, submitLabel: 'Save' },
    };

  const field = (key: string, f: Omit<TimeFieldView, 'error'>): TimeFieldView => ({
    ...f,
    error: st.errors[key] ?? null,
  });

  let fix: QuickEditView['fix'] = null;
  if (first) {
    const { fixes } = fixesFor(input, first);
    const chosen = fixes.find((f) => f.id === s.fix);
    fix = {
      label: 'Fix',
      options: fixes.map(({ id, label, description }) => ({ id, label, description })),
      selected: s.fix,
      other:
        chosen?.does.kind === 'typed'
          ? field('fixTime', {
              label:
                first.kind === 'notEnded' ? 'The day ended at' : `${capital(kindWord(first.entry))} ended at`,
              value: s.fixText,
              placeholder: 'e.g. 3:40 p.m.',
            })
          : null,
    };
  }

  const sel = s.selected ? day.entries.find((e) => e.id === s.selected) : undefined;
  const change: QuickEditView['change'] = sel
    ? {
        title: capital(sel.name),
        start: field(`start:${sel.id}`, {
          label: 'Starts',
          value: s.edits[sel.id]?.start ?? clockOn(sel.startMinute, date),
          fixed: sel.fromMidnight ? `Goes on from the day before, past midnight: change it there.` : null,
        }),
        end: field(`end:${sel.id}`, {
          label: 'Ends',
          value: s.edits[sel.id]?.end ?? (sel.endMinute === null ? '' : clockOn(sel.endMinute, date)),
          fixed: sel.running ? `Running now: ${person.first} ends it on the iPad.` : null,
        }),
        kind: null,
        addLunch: sel.type === 'work' && sel.endMinute !== null,
      }
    : null;

  const add: QuickEditView['add'] = {
    label: 'Add time',
    options: input.codes.map((c) => ({ id: c.code, label: `${addLabel(c)}${c.paid ? '' : ' (not paid)'}` })),
    selected: s.add,
    from: s.add ? field('addFrom', { label: 'From', value: s.addFrom, placeholder: 'e.g. 12 p.m.' }) : null,
    to: s.add
      ? field('addTo', {
          label: 'To',
          value: s.addTo,
          placeholder: 'e.g. 12:30 p.m.',
          description: '30 min after From if left blank',
        })
      : null,
  };

  const why: QuickEditView['why'] = {
    label: 'Why',
    options: REASONS,
    selected: s.reason,
    other:
      s.reason === 'other'
        ? { label: 'Say why', value: s.other, placeholder: 'A few words for the history' }
        : null,
  };

  const ask = st.ask
    ? {
        text: st.ask.text,
        label: st.ask.yes,
        confirmed: s.confirmed === st.ask.text,
        ...(st.ask.no ? { decline: st.ask.no } : {}),
      }
    : null;
  const n = st.changes.length;
  // kept through a clash: said over the fields, since the fix they came from may be gone
  const kept = st.changes.filter((p) => p.key.startsWith('kept:'));
  const keptWords = kept.length
    ? `Yours, not saved yet: ${stop(kept.map((p) => p.words).join('; '))} Save again to replace theirs.`
    : null;
  return {
    ...base,
    body: { kind: 'edit' },
    fix,
    change,
    add,
    why,
    ask,
    pending:
      keptWords && n > kept.length
        ? `${keptWords} Changes so far: ${n}, listed under Save.`
        : (keptWords ?? (n > 1 ? `Changes so far: ${n}, listed under Save.` : null)),
    conflict: s.conflict,
    footer: footerOf(input, s, st, sim),
    discardText: `Discard your ${n > 1 ? 'changes' : 'change'} to ${dayW}?`,
    isDirty: s.dirty,
  };
}

const addLabel = (c: NonJobCode) =>
  c.code === 'lunch'
    ? 'Lunch'
    : c.code === 'shop'
      ? 'At the shop'
      : c.code === 'supply_house'
        ? 'Supply house'
        : c.name;

function footerOf(
  input: QuickEditInput,
  s: QuickEditState,
  st: Stack,
  sim: { paidAfter: number; moves: string[] },
): QuickEditView['footer'] {
  const submitLabel = s.conflict ? 'Save again' : 'Save';
  const off = (note: string) => ({ note, canSave: false, submitLabel });
  const err = Object.values(st.errors)[0];
  if (err) return off(`Fix the time first: ${err.charAt(0).toLowerCase()}${err.slice(1)}`);
  if (!st.changes.length) return off(st.missing ? 'Type the time first.' : 'Nothing to save yet.');
  if (st.ask && s.confirmed !== st.ask.text)
    return off(st.ask.no ? 'Choose which you meant first.' : 'Confirm the time first.');
  if (!s.reason) return off('Choose why.');
  if (s.reason === 'other' && !s.other.trim()) return off('Say why in a few words.');
  const day = dayWords(input.day.date);
  const before = input.day.split.paid;
  const after = sim.paidAfter;
  const line = input.overtimeAfter ?? 480;
  const past = after > line ? `, ${minutesText(after - line)} past 8 h` : '';
  const what = st.changes.map((c) => c.words).join('; ');
  const paid =
    after === before
      ? `${day} stays ${minutesText(before)} paid${sim.moves.length ? `: ${sim.moves.join('; ')}` : ''}`
      : `${day} becomes ${minutesText(after)} paid (was ${minutesText(before)})`;
  return { note: `Save: ${what} · ${paid}${past}.`, canSave: true, submitLabel };
}

function titleOf(input: QuickEditInput, p: DayProblem | undefined): string {
  const { person, day } = input;
  const dayW = dayWords(day.date);
  if (!p) return `${person.name} · ${dayW}`;
  if (p.kind === 'notEnded') return `${person.first} never ended the day on ${dayW}`;
  const e = p.entry;
  if (p.kind === 'missed') return `${person.first}’s ${e.name} on ${dayW} never ended`;
  if (p.code === 'closed_by_non_job') {
    const next = day.entries[day.entries.indexOf(e) + 1];
    const tap =
      next?.nonJobCode === 'supply_house'
        ? 'a supply-house tap'
        : next?.nonJobCode === 'lunch'
          ? 'a lunch tap'
          : next?.nonJobCode === 'shop'
            ? 'a shop tap'
            : 'a non-job tap';
    return `${person.first}’s ${e.name} on ${dayW} was ended by ${tap} at ${clockOn(e.endMinute!, day.date)}`;
  }
  return `Check ${person.first}’s ${e.name} on ${dayW}: ${p.words}`;
}

function subOf(input: QuickEditInput, p: DayProblem | undefined): string {
  const { day } = input;
  const firstPart = day.entries[0];
  if (!firstPart) return '';
  const today = isToday(input);
  const bits = [
    `${today ? 'Today · started' : 'Started'} ${clockOn(firstPart.startMinute, day.date)}`,
    `${minutesText(day.split.paid)} paid${today ? ' so far' : p?.kind === 'missed' ? ', not counting the open part' : ''}`,
  ];
  if (day.entries.some((e) => (e.endMinute ?? 0) > 1440))
    bits.push(
      `${input.lateCall ? 'Late call · ' : ''}all on ${dayWords(day.date)}: past midnight counts on the day it started`,
    );
  return bits.join(' · ');
}

function notesOf(input: QuickEditInput, problems: DayProblem[]): QuickEditView['notes'] {
  const out: QuickEditView['notes'] = [];
  for (const p of problems.slice(1))
    out.push({
      text: `Also: ${p.kind === 'notEnded' ? 'the day was never ended' : p.kind === 'missed' ? `${p.entry.name} never ended` : `${p.entry.name}: ${p.words}`}.`,
      tone: 'bad',
    });
  const last = input.lastChange;
  if (last)
    out.push({
      text: stop(
        `Last change: ${last.by ?? 'someone'}, ${last.atWords}: ${last.changes.map((c) => c.what).join('; ') || last.reason}`,
      ),
      tone: 'muted',
    });
  if (input.device === 'unknown')
    out.push({
      text: `Whether ${input.person.first}’s iPad still holds taps isn’t shown yet (not built yet).`,
      tone: 'muted',
    });
  return out;
}

/** "Fri Oct 9 is in Sep 28 – Oct 11, locked by Kim Lau on Mon Oct 12 and sent to payroll by …" */
export function lockedWords(input: QuickEditInput): string {
  const p = input.period;
  const dayW = dayWords(input.day.date);
  if (!p) return `${dayW} is in a locked pay period. It can’t change.`;
  const on = (at: string | null) => (at ? ` on ${dayWords(localDateOf(at, input.timeZone))}` : '');
  const locked = `locked${p.lockedBy ? ` by ${p.lockedBy}` : ''}${on(p.lockedAt)}`;
  const sent =
    p.sentAt || p.sentBy ? ` and sent to payroll${p.sentBy ? ` by ${p.sentBy}` : ''}${on(p.sentAt)}` : '';
  return `${dayW} is in ${periodWords(p.startDate, p.endDate)}, ${locked}${sent}. It can’t change.`;
}

// ---- Save, and a refusal

export type QuickEditSave =
  | {
      action: 'time.editDay';
      input: { staffId: string; localDate: string; version: number; reason: string; changes: DayChange[] };
    }
  | {
      action: 'time.correct';
      input: {
        staffId: string;
        localDate: string;
        type: TimeEntry['type'];
        minutes: 0;
        reason: string;
        entryId: string;
        resolvesFlag: true;
      };
    };

/** What Save sends: the day's changes in one time.editDay, then any check settled as it stands. */
export function saveRequests(input: QuickEditInput, s: QuickEditState): QuickEditSave[] {
  const st = pendingOf(input, s);
  const reason =
    s.reason === 'other' ? s.other.trim() : (REASONS.find((r) => r.id === s.reason)?.label ?? '');
  const base = { staffId: input.staffId, localDate: input.day.date };
  // a part over 12 h was asked about and confirmed before Save: the server takes it as meant
  const changes = st.changes.flatMap((p) =>
    p.change.kind === 'correct'
      ? []
      : [
          p.change.kind !== 'addPart' && overTwelve(input, p.change)
            ? { ...p.change, overTwelveHours: true as const }
            : p.change,
        ],
  );
  const out: QuickEditSave[] = [];
  if (changes.length)
    out.push({ action: 'time.editDay', input: { ...base, version: s.version, reason, changes } });
  for (const p of st.changes)
    if (p.change.kind === 'correct')
      out.push({
        action: 'time.correct',
        input: {
          ...base,
          type: p.change.entry.type,
          minutes: 0,
          reason,
          entryId: p.change.entry.id,
          resolvesFlag: true,
        },
      });
  return out;
}

export interface Refusal {
  code: string;
  message: string;
  detail?: {
    reason?: string;
    version?: number;
    issues?: { path: (string | number)[]; message: string }[];
  };
}

/**
 * A refused save, back onto the dialog: someone saving first keeps every change and asks to save
 * again on the new version (the page reads time.day again to redraw); a field the server refused gets
 * its words; anything else is the failure line. `message` is what the dialog's failure line says.
 */
export function refused(
  input: QuickEditInput,
  s: QuickEditState,
  r: Refusal,
): { state: QuickEditState; message: string } {
  if (r.code === 'conflict' && r.detail?.reason === 'stale_version') {
    // Their change stands for now; this person's are kept as changes (not the fix or the fields they
    // came from, which belong to the day as it was), so Save again replaces theirs on the new version.
    const kept: KeptChange[] = pendingOf(input, s).changes.map((p) =>
      p.change.kind === 'correct' ? { kind: 'correct', entryId: p.change.entry.id } : p.change,
    );
    return {
      state: {
        ...s,
        version: r.detail.version ?? s.version,
        conflict: r.message,
        kept,
        fix: null,
        fixText: '',
        selected: null,
        edits: {},
        add: null,
        addFrom: '',
        addTo: '',
        server: {},
        dirty: true,
      },
      message: r.message,
    };
  }
  if (r.code === 'invalid_input' && r.detail?.issues?.length) {
    const st = pendingOf(input, s);
    const editDay = st.changes.filter((p) => p.change.kind !== 'correct');
    const server = { ...s.server };
    for (const issue of r.detail.issues) {
      const i = issue.path[0] === 'changes' ? Number(issue.path[1]) : NaN;
      const p = editDay[i];
      if (!p) continue;
      const field = issue.path[2];
      server[p.key === 'fix' ? 'fixTime' : p.key === 'addFrom' && field === 'to' ? 'addTo' : p.key] =
        issue.message;
    }
    // A fix chosen from the list has no field of its own: its words go on Another time's.
    const fixed = s.fix && server.fixTime && s.fix !== 'other' ? 'other' : s.fix;
    return {
      state: { ...s, server, fix: fixed },
      message: 'Fix the time first: the server said why under it.',
    };
  }
  return { state: s, message: r.message || 'Your changes didn’t save. They’re still here; try again.' };
}
